// Servicio de Roles Personalizados
// Maneja la gestión de roles personalizados en Firestore

import { getCollection, getDocument, updateDocument, deleteDocument } from './firestore.service'
import { doc, setDoc, collection, Timestamp } from 'firebase/firestore'
import { db } from './firebase.config'

const ROLES_COLLECTION = 'roles'

/**
 * Obtener todos los roles personalizados
 * @returns {Promise<Array>} Array de roles
 */
export const getAllRoles = async () => {
  try {
    const roles = await getCollection(
      ROLES_COLLECTION,
      [],
      'createdAt',
      'desc'
    )
    return roles
  } catch (error) {
    console.error('Error obteniendo roles:', error)
    throw error
  }
}

/**
 * Obtener un rol por ID
 * @param {string} roleId - ID del rol
 * @returns {Promise<Object|null>} Rol o null si no existe
 */
export const getRole = async (roleId) => {
  try {
    return await getDocument(ROLES_COLLECTION, roleId)
  } catch (error) {
    console.error('Error obteniendo rol:', error)
    throw error
  }
}

/**
 * Crear un nuevo rol personalizado
 * @param {Object} roleData - Datos del rol
 * @param {string} roleData.name - Nombre del rol
 * @param {string} roleData.description - Descripción del rol
 * @param {Object} roleData.permissions - Permisos del rol { module: [actions] }
 * @param {boolean} roleData.active - Si el rol está activo
 * @returns {Promise<Object>} Rol creado
 */
export const createRole = async (roleData) => {
  try {
    const { name, description = '', permissions = {}, active = true } = roleData

    // Validar que el nombre no esté vacío
    if (!name || !name.trim()) {
      throw new Error('El nombre del rol es requerido')
    }

    // Crear referencia a un nuevo documento con ID generado automáticamente
    const rolesCollection = collection(db, ROLES_COLLECTION)
    const newRoleRef = doc(rolesCollection)
    const roleId = newRoleRef.id

    const roleDoc = {
      id: roleId,
      name: name.trim(),
      description: description.trim(),
      permissions: permissions,
      active: active,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    }

    // Guardar en Firestore
    await setDoc(newRoleRef, roleDoc)

    return {
      id: roleId,
      ...roleDoc
    }
  } catch (error) {
    console.error('Error creando rol:', error)
    throw error
  }
}

/**
 * Actualizar un rol personalizado
 * @param {string} roleId - ID del rol
 * @param {Object} updates - Datos a actualizar
 * @returns {Promise<void>}
 */
export const updateRole = async (roleId, updates) => {
  try {
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now()
    }
    await updateDocument(ROLES_COLLECTION, roleId, updateData)
  } catch (error) {
    console.error('Error actualizando rol:', error)
    throw error
  }
}

/**
 * Eliminar un rol personalizado
 * @param {string} roleId - ID del rol
 * @returns {Promise<void>}
 */
export const deleteRole = async (roleId) => {
  try {
    await deleteDocument(ROLES_COLLECTION, roleId)
  } catch (error) {
    console.error('Error eliminando rol:', error)
    throw error
  }
}

/**
 * Verificar si un nombre de rol ya existe
 * @param {string} name - Nombre del rol
 * @param {string} excludeId - ID del rol a excluir (para edición)
 * @returns {Promise<boolean>} true si existe, false si no
 */
export const roleNameExists = async (name, excludeId = null) => {
  try {
    const roles = await getAllRoles()
    const normalizedName = name.trim().toLowerCase()
    
    return roles.some(role => {
      if (excludeId && role.id === excludeId) return false
      return role.name.toLowerCase() === normalizedName
    })
  } catch (error) {
    console.error('Error verificando nombre de rol:', error)
    throw error
  }
}

export default {
  getAllRoles,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  roleNameExists
}

